import api from "./api";

export const MAQUINA_TIPOS = [
  "Escavadeira",
  "Retroescavadeira",
  "Motoniveladora",
  "Pá Carregadeira",
  "Rolo Compactador",
  "Caminhão Basculante",
  "Caminhão Pipa",
  "Munck",
  "Gerador",
  "Compressor",
  "Trator",
] as const;

export type AtivoTipo = "maquinario" | "equipamento";
export type FiltroTipoAtivo = "todos" | AtivoTipo;

export const EQUIPAMENTO_TIPOS = [
  "Container",
  "Baú",
  "Módulo sanitário",
  "Escritório",
  "Depósito",
  "Gerador",
  "Outro",
] as const;

export type EquipamentoTipo = (typeof EQUIPAMENTO_TIPOS)[number];

export const ATIVO_TIPO_LABELS: Record<AtivoTipo, string> = {
  maquinario: "Maquinário",
  equipamento: "Equipamento",
};

export const FILTRO_TIPO_OPTIONS: Array<{ value: FiltroTipoAtivo; label: string }> = [
  { value: "todos", label: "Todos os tipos" },
  { value: "maquinario", label: "Maquinário" },
  { value: "equipamento", label: "Equipamentos" },
];

export function normalizeAtivoTipo(t: string | undefined | null): AtivoTipo {
  if (t === "equipamento" || t === "container") return "equipamento";
  return "maquinario";
}

/** Classifica maquinário vs equipamento usando tipoAtivo e categoria (Container, Baú…). */
export function resolveAtivoTipo(ativo: Pick<MaquinarioDto, "tipoAtivo" | "tipo">): AtivoTipo {
  const fromField = normalizeAtivoTipo(ativo.tipoAtivo);
  if (fromField === "equipamento") return "equipamento";
  const cat = (ativo.tipo ?? "").trim();
  if (cat && (EQUIPAMENTO_TIPOS as readonly string[]).includes(cat)) return "equipamento";
  return "maquinario";
}

export const COMBUSTIVEL_TIPOS = ["diesel_s10", "diesel_s500", "gasolina", "etanol"] as const;
export type CombustivelTipo = (typeof COMBUSTIVEL_TIPOS)[number];

export const COMBUSTIVEL_LABELS: Record<CombustivelTipo, string> = {
  diesel_s10: "Diesel S10",
  diesel_s500: "Diesel S500",
  gasolina: "Gasolina",
  etanol: "Etanol",
};

export type RegistroHorimetroTipo = "inicio" | "final" | "parada";

export const REGISTRO_HORIMETRO_LABELS: Record<RegistroHorimetroTipo, string> = {
  inicio: "Início",
  final: "Final",
  parada: "Parada",
};

export type MaquinaDocumentoCategoria = "crlv" | "nota_fiscal" | "manual" | "foto" | "outros";

export const DOCUMENTO_CATEGORIA_LABELS: Record<MaquinaDocumentoCategoria, string> = {
  crlv: "CRLV",
  nota_fiscal: "Nota Fiscal",
  manual: "Manual",
  foto: "Foto da máquina",
  outros: "Outros documentos",
};

export interface MaquinarioDocumentoDto {
  id: string;
  categoria: MaquinaDocumentoCategoria;
  storedFile: string;
  originalName: string;
  uploadedAt: string;
  usuarioId: string;
  usuarioNome: string;
}

export type MaquinarioStatus = "disponivel" | "locado" | "manutencao" | "inativo";
export type LocacaoStatus = "ativa" | "encerrada" | "cancelada";
export type ManutencaoTipo = "preventiva" | "corretiva" | "revisao" | "troca_oleo";
export type ManutencaoStatus = "aberta" | "em_andamento" | "aguardando_peca" | "finalizada";
export type ManutencaoPrioridade = "baixa" | "media" | "alta";

export const MANUTENCAO_TIPO_LABELS: Record<ManutencaoTipo, string> = {
  preventiva: "Preventiva",
  corretiva: "Corretiva",
  revisao: "Revisão",
  troca_oleo: "Troca de Óleo",
};

export const MANUTENCAO_PRIORIDADE_LABELS: Record<ManutencaoPrioridade, string> = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
};

export type LocacaoTimelineEventType =
  | "maquina_criada"
  | "maquina_editada"
  | "locacao_iniciada"
  | "locacao_encerrada"
  | "locacao_cancelada"
  | "manutencao_aberta"
  | "manutencao_concluida"
  | "medicao_registrada"
  | "horimetro_registrado"
  | "abastecimento_registrado";

export interface LocacaoTimelineEvent {
  id: string;
  type: LocacaoTimelineEventType;
  at: string;
  userId: string;
  userName: string;
  note?: string;
}

export const LOCACAO_TIMELINE_LABELS: Record<LocacaoTimelineEventType, string> = {
  maquina_criada: "Máquina criada",
  maquina_editada: "Máquina editada",
  locacao_iniciada: "Locação iniciada",
  locacao_encerrada: "Locação encerrada",
  locacao_cancelada: "Locação cancelada",
  manutencao_aberta: "Manutenção aberta",
  manutencao_concluida: "Manutenção concluída",
  medicao_registrada: "Medição registrada",
  horimetro_registrado: "Horímetro registrado",
  abastecimento_registrado: "Abastecimento registrado",
};

export const MAQUINARIO_STATUS_LABELS: Record<MaquinarioStatus, string> = {
  disponivel: "Disponível",
  locado: "Locado",
  manutencao: "Em manutenção",
  inativo: "Inativo",
};

export const LOCACAO_STATUS_LABELS: Record<LocacaoStatus, string> = {
  ativa: "Ativa",
  encerrada: "Encerrada",
  cancelada: "Cancelada",
};

export const MANUTENCAO_STATUS_LABELS: Record<ManutencaoStatus, string> = {
  aberta: "Aberta",
  em_andamento: "Em andamento",
  aguardando_peca: "Aguardando peça",
  finalizada: "Finalizada",
};

export interface MaquinarioDto {
  id: string;
  nome: string;
  codigoInterno: string;
  tipoAtivo: AtivoTipo;
  tipo: string;
  fabricante: string;
  modelo: string;
  anoFabricacao: number | null;
  placa: string;
  numeroSerie: string;
  observacoes: string;
  horimetroAtual: number;
  odometroAtual: number;
  status: MaquinarioStatus;
  consumoLimiteLph?: number;
  timeline: LocacaoTimelineEvent[];
  documentos: MaquinarioDocumentoDto[];
  createdAt: string;
  updatedAt: string;
}

export interface MedicaoMaquinaDto {
  id: string;
  maquinaId: string;
  horimetroAnterior: number;
  horimetroAtual: number;
  odometroAnterior: number;
  odometroAtual: number;
  dataRegistro: string;
  usuarioId: string;
  usuarioNome: string;
  observacao: string;
}

export interface LocacaoDto {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  costCenterId: string;
  costCenterLabel: string;
  obraId: string;
  obraNome: string;
  responsavelId: string;
  responsavelNome: string;
  dataInicio: string;
  dataFimPrevista: string | null;
  dataFimReal: string | null;
  horimetroSaida: number;
  /** Último horímetro (final aprovado) durante a locação ativa. */
  horimetroAtual?: number;
  odometroSaida: number;
  horimetroRetorno: number | null;
  odometroRetorno: number | null;
  status: LocacaoStatus;
  observacoes: string;
  contratoId?: string;
  contratoLabel?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ManutencaoDto {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  tipo: ManutencaoTipo;
  descricao: string;
  horimetroAtual: number;
  prioridade: ManutencaoPrioridade;
  responsavelId: string;
  responsavelNome: string;
  dataAbertura: string;
  dataPrevista: string | null;
  dataConclusao: string | null;
  status: ManutencaoStatus;
  statusAprovacao?: AprovacaoStatus;
  motivoRejeicao?: string;
  origemOperador?: boolean;
  custoEstimado?: number;
  createdAt: string;
  updatedAt: string;
}

export interface RegistroHorimetroDto {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  tipo: RegistroHorimetroTipo;
  horimetroAtual: number;
  horimetroInformado: number;
  observacao: string;
  dataHora: string;
  usuarioId: string;
  usuarioNome: string;
  createdAt: string;
  contratoId?: string;
  statusAprovacao?: AprovacaoStatus;
  motivoRejeicao?: string;
  origemOperador?: boolean;
  fotos?: RegistroHorimetroFotoDto[];
  registroInicioId?: string;
  horasTrabalhadas?: number;
}

export interface HorimetroSessaoAbertaDto {
  registroInicioId: string;
  maquinaId: string;
  contratoId?: string;
  horimetroInicio: number;
  dataInicio: string;
}

export interface HorimetroEstadoDto {
  sessaoAberta: HorimetroSessaoAbertaDto | null;
  proximoTipo: RegistroHorimetroTipo;
  horimetroBase: number;
  minHorimetroFinal: number | null;
}

export interface RegistroHorimetroFotoDto {
  id: string;
  storedFile: string;
  originalName: string;
  mimeType: string;
  uploadedAt: string;
  usuarioId: string;
  usuarioNome: string;
}

export interface AbastecimentoFotoDto {
  id: string;
  storedFile: string;
  originalName: string;
  mimeType: string;
  uploadedAt: string;
  usuarioId: string;
  usuarioNome: string;
}

export interface AbastecimentoDto {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  data: string;
  horimetroAtual: number;
  litros: number;
  valorTotal: number;
  tipoCombustivel: CombustivelTipo;
  consumoMedio: number | null;
  litrosPorHora: number | null;
  observacao?: string;
  fotos?: AbastecimentoFotoDto[];
  usuarioId: string;
  usuarioNome: string;
  createdAt: string;
  contratoId?: string;
}

export interface PeriodRange {
  startDate: string;
  endDate: string;
}

export interface OperationalMetricsDto {
  horasTrabalhadas: number;
  abastecimentosCount: number;
  litrosAbastecidos: number;
  consumoMedioGeral: number;
  horasPorDia: Array<{ date: string; horas: number }>;
  consumoPorContrato: Array<{
    contratoId: string;
    codigoContrato: string;
    clienteSiengeNome: string;
    litros: number;
    horas: number;
    litrosPorHora: number;
  }>;
  consumoPorMaquina: Array<{
    maquinaId: string;
    maquinaNome: string;
    litros: number;
    horas: number;
    litrosPorHora: number;
    consumoLimiteLph: number;
  }>;
}

export interface OperationalDeltasDto {
  horasTrabalhadas: number;
  abastecimentos: number;
  litrosAbastecidos: number;
  consumoMedio: number;
}

export interface LocacoesDashboardDto {
  totalMaquinas: number;
  disponiveis: number;
  locadas: number;
  emManutencao: number;
  inativas: number;
  locacoesAtivas: number;
  manutencoesAbertas: number;
  horasTrabalhadas: number;
  abastecimentos: number;
  litrosAbastecidos: number;
  consumoMedio: number;
  maquinasAtivas: number;
}

export interface PeriodRange {
  startDate: string;
  endDate: string;
}

export interface LocacoesScope {
  clienteSiengeId?: string;
  contratoId?: string;
  maquinaId?: string;
  contratoStatus?: "ativo" | "encerrado";
  tipoContrato?: AtivoTipo;
}

export interface ContratoFaturamentoDto {
  horasTrabalhadas: number;
  valorPeriodo: number;
  porHoras: boolean;
  valorFixo: number | null;
  quantidadeCarradas?: number;
  porCarradas?: boolean;
}

export type LocacoesAccessMode = "full" | "operador";

export interface LocacoesSnapshot {
  accessMode?: LocacoesAccessMode;
  canApproveLocacoes?: boolean;
  operadorContratoIds?: string[];
  dashboard: LocacoesDashboardDto;
  metrics: OperationalMetricsDto;
  deltas: OperationalDeltasDto;
  biSummary?: LocacoesBiSummary;
  filaAprovacao?: FilaAprovacaoItem[];
  pipelineCards?: ContractPipelineCard[];
  period: PeriodRange;
  scope?: LocacoesScope | null;
  scopeLabel?: string | null;
  maquinarios: MaquinarioDto[];
  /** Máquinas do escopo ativo (dashboard/BI); frota completa em `maquinarios`. */
  maquinariosScoped?: MaquinarioDto[];
  operadores: OperadorCatalogDto[];
  contratos: ContratoDto[];
  locacoes: LocacaoDto[];
  manutencoes: ManutencaoDto[];
  registrosHorimetro: RegistroHorimetroDto[];
  abastecimentos: AbastecimentoDto[];
}

export const CONTRATO_MODELOS = [
  "Locação mensal",
  "Locação diária",
  "Locação por horímetro",
  "Locação por carradas",
  "Locação de equipamento (mensal)",
  "Locação de container (mensal)",
  "Locação por obra",
  "Locação com operador",
  "Outro",
] as const;

export const CONTRATO_MODELO_POR_HORAS = "Locação por horímetro" as const;
export const CONTRATO_MODELO_POR_CARRADAS = "Locação por carradas" as const;
export const CONTRATO_MODELO_POR_CARRADAS_SLUG = "locacao_por_carradas" as const;
export const CONTRATO_MODELO_EQUIPAMENTO = "Locação de equipamento (mensal)" as const;
export const CONTRATO_MODELO_CONTAINER = "Locação de container (mensal)" as const;

export function isContratoPorHoras(modelo: string): boolean {
  return modelo.trim() === CONTRATO_MODELO_POR_HORAS;
}

export function isContratoPorCarradas(modelo: string): boolean {
  return modelo.trim() === CONTRATO_MODELO_POR_CARRADAS;
}

export interface ValorCarradaVigencia {
  id: string;
  valorUnitario: number;
  observacao?: string;
  /** Momento em que este valor passou a valer (ISO datetime). */
  vigenteDesde: string;
  /** Momento em que deixou de valer; null = ainda vigente. */
  vigenteAte: string | null;
  registradoPorId?: string;
  registradoPorNome?: string;
  /** @deprecated Compat histórico antigo. */
  dataInicio?: string;
  /** @deprecated Compat histórico antigo. */
  dataFim?: string | null;
}

function vigenciaInstantMs(v: ValorCarradaVigencia): number {
  const raw = v.vigenteDesde || (v.dataInicio ? `${v.dataInicio}T00:00:00.000Z` : "");
  const t = Date.parse(raw);
  return Number.isNaN(t) ? 0 : t;
}

function vigenciaAteMs(v: ValorCarradaVigencia): number | null {
  if (v.vigenteAte) {
    const t = Date.parse(v.vigenteAte);
    return Number.isNaN(t) ? null : t;
  }
  if (v.dataFim) {
    const t = Date.parse(`${v.dataFim}T23:59:59.999Z`);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Valor unitário vigente no instante (ISO datetime). Sem argumento = agora. */
export function getValorCarradaVigente(
  vigencias: ValorCarradaVigencia[] | undefined,
  atIso?: string,
  _horaHHmm?: string,
): number | null {
  const atRaw = (atIso ?? new Date().toISOString()).trim();
  const at =
    /^\d{4}-\d{2}-\d{2}$/.test(atRaw) ? `${atRaw}T23:59:59.999Z` : atRaw;
  const atMs = Date.parse(at);
  if (Number.isNaN(atMs)) return null;
  let best: ValorCarradaVigencia | null = null;
  for (const v of vigencias ?? []) {
    if (!(v.valorUnitario > 0)) continue;
    const desde = vigenciaInstantMs(v);
    if (atMs < desde) continue;
    const ate = vigenciaAteMs(v);
    if (ate != null && atMs >= ate) continue;
    if (!best || vigenciaInstantMs(v) > vigenciaInstantMs(best)) best = v;
  }
  return best ? best.valorUnitario : null;
}

export function sortVigenciasValorCarradaDesc(list: ValorCarradaVigencia[]): ValorCarradaVigencia[] {
  return [...list].sort((a, b) => vigenciaInstantMs(b) - vigenciaInstantMs(a));
}

export function vigenciaValorEstaAberta(v: ValorCarradaVigencia): boolean {
  return !v.vigenteAte && !v.dataFim;
}

export function isCombustivelPagoCliente(
  contrato: Pick<ContratoDto, "combustivelPagoCliente"> | null | undefined,
): boolean {
  return contrato?.combustivelPagoCliente === true;
}

export const CONTRATO_MODELOS_MAQUINARIO = [...CONTRATO_MODELOS] as const;

export const CONTRATO_MODELOS_EQUIPAMENTO = CONTRATO_MODELOS.filter(
  (m) => m !== CONTRATO_MODELO_POR_HORAS && m !== CONTRATO_MODELO_POR_CARRADAS,
) as readonly (typeof CONTRATO_MODELOS)[number][];

export function modelosContratoPorTipo(tipo: AtivoTipo): readonly string[] {
  return tipo === "equipamento" ? CONTRATO_MODELOS_EQUIPAMENTO : CONTRATO_MODELOS_MAQUINARIO;
}

export function isContratoModeloMensalEquipamento(modelo: string): boolean {
  const m = modelo.trim();
  return m === CONTRATO_MODELO_EQUIPAMENTO || m === CONTRATO_MODELO_CONTAINER;
}

export function isContratoEquipamento(
  contrato: Pick<ContratoDto, "tipoContrato" | "modeloContrato">,
): boolean {
  const modelo = contrato.modeloContrato.trim();
  return (
    normalizeAtivoTipo(contrato.tipoContrato) === "equipamento" ||
    modelo === CONTRATO_MODELO_EQUIPAMENTO ||
    modelo === CONTRATO_MODELO_CONTAINER
  );
}

export const isContratoContainer = isContratoEquipamento;

export function isEquipamentoAtivo(ativo: Pick<MaquinarioDto, "tipoAtivo" | "tipo">): boolean {
  return resolveAtivoTipo(ativo) === "equipamento";
}

export const isContainerAtivo = isEquipamentoAtivo;

export type ContratoStatus =
  | "ativo"
  | "esperando_ativacao"
  | "inativo"
  | "encerrado"
  | "cancelado";

export function isContratoOperacional(status: ContratoStatus): boolean {
  return status === "ativo";
}

export type ContratoPipelineFase =
  | "captacao"
  | "setup"
  | "vigente"
  | "proximo_vencimento"
  | "encerrado";

export type AprovacaoStatus = "pendente" | "aprovado" | "rejeitado";

export interface FilaAprovacaoItem {
  id: string;
  tipo: "horimetro" | "manutencao" | "carrada";
  titulo: string;
  subtitulo: string;
  maquinaId: string;
  maquinaNome: string;
  contratoId?: string;
  contratoLabel?: string;
  solicitanteNome: string;
  dataHora: string;
  statusAprovacao: AprovacaoStatus;
  motivoRejeicao?: string;
  detalhe?: string;
  fotosCount?: number;
}

export interface LocacoesBiSummary {
  faturamentoTotal: number;
  gastosTotal: number;
  gastosCombustivel: number;
  gastosManutencao: number;
  margem: number;
  margemPercentual: number;
  rentabilidadePorContrato: Array<{
    contratoId: string;
    clienteNome: string;
    faturamento: number;
    gastos: number;
    margem: number;
  }>;
  eficienciaMaquinas: Array<{
    maquinaId: string;
    maquinaNome: string;
    maquinaTipo: string;
    litrosPorHora: number;
    limiteLph: number;
    alertaAltoConsumo: boolean;
  }>;
}

export interface ContractPipelineCard {
  id: string;
  codigoContrato: string;
  pipelineFase: ContratoPipelineFase;
  modeloContrato: string;
  clienteSiengeNome: string;
  clienteSiengeDocument: string;
  /** Texto livre do contrato (informações básicas), usado para identificar o card. */
  infoBasicos?: string;
  valorContrato: number;
  valorPotencial?: number;
  /** Faturamento acumulado em todos os registros do contrato. */
  valorPeriodo?: number;
  horasPeriodo?: number;
  registrosCount?: number;
  porHoras?: boolean;
  maquinaIds: string[];
  maquinaNomes: string[];
  operadoresCount: number;
  dataInicio: string;
  dataFimPrevista: string | null;
  ultimoFollowUp?: string;
  motivoEncerramento?: string;
  status: ContratoStatus;
  saudeApontamentos: "ok" | "atrasado" | "sem_dados";
  pendencias: string[];
}

export interface ContratoOperadorDto {
  id: string;
  nome: string;
  documento: string;
  telefone: string;
  observacao: string;
  catalogOperadorId?: string;
}

export interface OperadorCatalogDto {
  id: string;
  nome: string;
  documento: string;
  telefone: string;
  observacao: string;
  usuarioId?: string;
  linkedUserName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OperadorFormValues {
  nome: string;
  documento: string;
  telefone: string;
  observacao: string;
  usuarioId?: string;
}

export interface ContratoHistoricoEntryDto {
  id: string;
  at: string;
  userId: string;
  userName: string;
  justificativa: string;
  alteracoes: string[];
}

export interface ContratoDto {
  id: string;
  codigoContrato: string;
  codigoSequencial: number;
  codigoAno: number;
  modeloContrato: string;
  clienteSiengeId: string;
  clienteSiengeNome: string;
  clienteSiengeDocument: string;
  valorContrato: number;
  valorHora: number;
  valorHoraPorMaquina: Record<string, number>;
  valorCarradaVigencias?: ValorCarradaVigencia[];
  maquinaIds: string[];
  infoBasicos: string;
  operadores: ContratoOperadorDto[];
  costCenterId: string;
  costCenterLabel: string;
  dataInicio: string;
  dataFimPrevista: string | null;
  status: ContratoStatus;
  pipelineFase?: ContratoPipelineFase;
  valorPotencial?: number;
  ultimoFollowUp?: string;
  motivoEncerramento?: string;
  historico?: ContratoHistoricoEntryDto[];
  tipoContrato: AtivoTipo;
  valorMensal: number | null;
  combustivelPagoCliente?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContratoFormValues {
  modeloContrato: string;
  clienteSiengeId: string;
  clienteSiengeNome: string;
  clienteSiengeDocument: string;
  valorContrato: number;
  valorHora: number;
  valorHoraPorMaquina: Record<string, number>;
  valorCarradaVigencias: ValorCarradaVigencia[];
  valorMensal: number;
  tipoContrato?: AtivoTipo;
  maquinaIds: string[];
  infoBasicos: string;
  costCenterId: string;
  costCenterLabel: string;
  dataInicio: string;
  dataFimPrevista: string;
  status?: ContratoStatus;
  combustivelPagoCliente?: boolean;
  /** Obrigatório ao editar contrato (auditoria). */
  justificativaAlteracao?: string;
}

export interface SiengeClienteOption {
  id: string;
  name: string;
  documentNumber: string;
}

export interface MaquinarioFormValues {
  nome: string;
  codigoInterno: string;
  tipoAtivo: AtivoTipo;
  tipo: string;
  fabricante: string;
  modelo: string;
  anoFabricacao: string;
  placa: string;
  numeroSerie: string;
  observacoes: string;
  horimetroAtual: number;
  odometroAtual: number;
  status: MaquinarioStatus;
  /** Limite L/h para alerta de alto consumo */
  consumoLimiteLph?: number;
}

export interface LocacaoFormValues {
  maquinaId: string;
  contratoId: string;
  costCenterId: string;
  costCenterLabel: string;
  responsavelId: string;
  responsavelNome: string;
  dataInicio: string;
  dataFimPrevista: string;
  observacoes: string;
}

function assertSuccess<T extends { success: boolean; error?: string }>(data: T): T {
  if (!data.success) throw new Error(data.error ?? "Operação falhou.");
  return data;
}

export function extractApiError(e: unknown, fallback: string): string {
  const ax = e as { response?: { data?: { error?: string }; status?: number }; message?: string };
  if (ax.response?.data?.error) return ax.response.data.error;
  if (ax.response?.status === 403) return "Sem permissão para Semog Locações.";
  if (ax.response?.status === 404) {
    const err = ax.response?.data?.error ?? "";
    if (/rota n/i.test(err)) {
      return "Rota de locações indisponível. Reinicie o servidor Node (npm start) e recarregue a página.";
    }
    return err || "Recurso não encontrado. Verifique se o servidor foi atualizado.";
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export async function fetchLocacoesSnapshot(
  period?: PeriodRange,
  scope?: LocacoesScope,
): Promise<LocacoesSnapshot> {
  const params: Record<string, string> = {};
  if (period) {
    params.startDate = period.startDate;
    params.endDate = period.endDate;
  }
  if (scope?.clienteSiengeId) params.clienteSiengeId = scope.clienteSiengeId;
  if (scope?.contratoId) params.contratoId = scope.contratoId;
  if (scope?.maquinaId) params.maquinaId = scope.maquinaId;
  if (scope?.contratoStatus) params.contratoStatus = scope.contratoStatus;
  if (scope?.tipoContrato) params.tipoContrato = scope.tipoContrato;
  const { data } = await api.get<LocacoesSnapshot & { success: boolean; error?: string }>("/api/locacoes", {
    params,
  });
  return assertSuccess(data);
}

export async function fetchContratoFaturamento(contratoId: string, period: PeriodRange) {
  const { data } = await api.get<{
    success: boolean;
    period: PeriodRange;
    faturamento: ContratoFaturamentoDto;
    error?: string;
  }>(`/api/locacoes/contratos/${contratoId}/faturamento`, { params: period });
  assertSuccess(data);
  return data;
}

export async function fetchMaquinarioDetail(id: string): Promise<{
  maquinario: MaquinarioDto;
  medicoes: MedicaoMaquinaDto[];
}> {
  const { data } = await api.get<{
    success: boolean;
    maquinario: MaquinarioDto;
    medicoes: MedicaoMaquinaDto[];
    error?: string;
  }>(`/api/locacoes/maquinarios/${id}`);
  assertSuccess(data);
  return { maquinario: data.maquinario, medicoes: data.medicoes };
}

export async function createMaquinario(body: MaquinarioFormValues): Promise<MaquinarioDto> {
  const { data } = await api.post<{ success: boolean; maquinario: MaquinarioDto; error?: string }>(
    "/api/locacoes/maquinarios",
    {
      ...body,
      anoFabricacao: body.anoFabricacao.trim() ? Number(body.anoFabricacao) : null,
    },
  );
  assertSuccess(data);
  return data.maquinario;
}

export async function updateMaquinario(id: string, body: Partial<MaquinarioFormValues>): Promise<MaquinarioDto> {
  const payload: Record<string, unknown> = { ...body };
  if (body.anoFabricacao !== undefined) {
    payload.anoFabricacao = body.anoFabricacao.trim() ? Number(body.anoFabricacao) : null;
  }
  const { data } = await api.patch<{ success: boolean; maquinario: MaquinarioDto; error?: string }>(
    `/api/locacoes/maquinarios/${id}`,
    payload,
  );
  assertSuccess(data);
  return data.maquinario;
}

export async function deleteMaquinario(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(`/api/locacoes/maquinarios/${id}`);
  assertSuccess(data);
}

export async function fetchOperadores(): Promise<OperadorCatalogDto[]> {
  const { data } = await api.get<{ success: boolean; operadores: OperadorCatalogDto[]; error?: string }>(
    "/api/locacoes/operadores",
  );
  assertSuccess(data);
  return data.operadores ?? [];
}

export async function createOperador(body: OperadorFormValues): Promise<OperadorCatalogDto> {
  const { data } = await api.post<{ success: boolean; operador: OperadorCatalogDto; error?: string }>(
    "/api/locacoes/operadores",
    body,
  );
  assertSuccess(data);
  return data.operador;
}

export async function updateOperador(id: string, body: Partial<OperadorFormValues>): Promise<OperadorCatalogDto> {
  const { data } = await api.patch<{ success: boolean; operador: OperadorCatalogDto; error?: string }>(
    `/api/locacoes/operadores/${id}`,
    body,
  );
  assertSuccess(data);
  return data.operador;
}

export async function deleteOperador(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(`/api/locacoes/operadores/${id}`);
  assertSuccess(data);
}

export async function createLocacao(body: LocacaoFormValues): Promise<LocacaoDto> {
  const { contratoId, ...rest } = body;
  const { data } = await api.post<{ success: boolean; locacao: LocacaoDto; error?: string }>(
    "/api/locacoes/locacoes",
    {
      ...rest,
      dataFimPrevista: body.dataFimPrevista.trim() || null,
      ...(contratoId.trim() ? { contratoId: contratoId.trim() } : {}),
    },
  );
  assertSuccess(data);
  return data.locacao;
}

export async function encerrarLocacao(
  id: string,
  body: { horimetroRetorno: number; odometroRetorno: number; dataFimReal?: string },
): Promise<LocacaoDto> {
  const { data } = await api.post<{ success: boolean; locacao: LocacaoDto; error?: string }>(
    `/api/locacoes/locacoes/${id}/encerrar`,
    body,
  );
  assertSuccess(data);
  return data.locacao;
}

export async function cancelarLocacao(id: string, motivo?: string): Promise<LocacaoDto> {
  const { data } = await api.post<{ success: boolean; locacao: LocacaoDto; error?: string }>(
    `/api/locacoes/locacoes/${id}/cancelar`,
    { motivo: motivo ?? "" },
  );
  assertSuccess(data);
  return data.locacao;
}

export async function createManutencao(body: {
  maquinaId: string;
  tipo: ManutencaoTipo;
  descricao: string;
  dataAbertura: string;
  horimetroAtual?: number;
  prioridade?: ManutencaoPrioridade;
  responsavelId?: string;
  responsavelNome?: string;
  dataPrevista?: string | null;
  custoEstimado?: number;
}): Promise<ManutencaoDto> {
  const { data } = await api.post<{ success: boolean; manutencao: ManutencaoDto; error?: string }>(
    "/api/locacoes/manutencoes",
    body,
  );
  assertSuccess(data);
  return data.manutencao;
}

export async function updateManutencaoStatus(id: string, status: ManutencaoStatus): Promise<ManutencaoDto> {
  const { data } = await api.patch<{ success: boolean; manutencao: ManutencaoDto; error?: string }>(
    `/api/locacoes/manutencoes/${id}/status`,
    { status },
  );
  assertSuccess(data);
  return data.manutencao;
}

export async function concluirManutencao(id: string, dataConclusao?: string): Promise<ManutencaoDto> {
  const { data } = await api.post<{ success: boolean; manutencao: ManutencaoDto; error?: string }>(
    `/api/locacoes/manutencoes/${id}/concluir`,
    { dataConclusao: dataConclusao ?? "" },
  );
  assertSuccess(data);
  return data.manutencao;
}

export async function updateManutencao(
  id: string,
  body: {
    tipo?: ManutencaoTipo;
    descricao?: string;
    horimetroAtual?: number;
    prioridade?: ManutencaoPrioridade;
    responsavelId?: string;
    responsavelNome?: string;
    dataAbertura?: string;
    dataPrevista?: string | null;
    dataConclusao?: string | null;
    custoEstimado?: number;
  },
): Promise<ManutencaoDto> {
  const { data } = await api.patch<{ success: boolean; manutencao: ManutencaoDto; error?: string }>(
    `/api/locacoes/manutencoes/${id}`,
    body,
  );
  assertSuccess(data);
  return data.manutencao;
}

export async function deleteManutencao(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(`/api/locacoes/manutencoes/${id}`);
  assertSuccess(data);
}

export async function fetchLocacoesUsuarios(): Promise<Array<{ id: string; displayName: string; username: string }>> {
  const { data } = await api.get<{
    success: boolean;
    users: Array<{ id: string; displayName: string; username: string }>;
    error?: string;
  }>("/api/locacoes/usuarios");
  assertSuccess(data);
  return data.users;
}

export async function uploadMaquinarioDocumento(
  maquinaId: string,
  categoria: MaquinaDocumentoCategoria,
  arquivo: File,
): Promise<MaquinarioDocumentoDto> {
  const fd = new FormData();
  fd.append("categoria", categoria);
  fd.append("arquivo", arquivo);
  const { data } = await api.post<{ success: boolean; documento: MaquinarioDocumentoDto; error?: string }>(
    `/api/locacoes/maquinarios/${maquinaId}/documentos`,
    fd,
  );
  assertSuccess(data);
  return data.documento;
}

export async function deleteMaquinarioDocumento(maquinaId: string, documentoId: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/maquinarios/${maquinaId}/documentos/${documentoId}`,
  );
  assertSuccess(data);
}

export function maquinarioDocumentoUrl(maquinaId: string, documentoId: string): string {
  return `/api/locacoes/maquinarios/${maquinaId}/documentos/${documentoId}`;
}

export async function createRegistroHorimetro(body: {
  maquinaId: string;
  tipo: RegistroHorimetroTipo;
  horimetroInformado: number;
  observacao?: string;
  dataHora?: string;
  contratoId?: string;
}): Promise<RegistroHorimetroDto> {
  const { data } = await api.post<{ success: boolean; registro: RegistroHorimetroDto; error?: string }>(
    "/api/locacoes/registros-horimetro",
    body,
  );
  assertSuccess(data);
  return data.registro;
}

export async function fetchHorimetroEstado(
  maquinaId: string,
  contratoId?: string,
): Promise<HorimetroEstadoDto> {
  const params = new URLSearchParams({ maquinaId });
  if (contratoId) params.set("contratoId", contratoId);
  const { data } = await api.get<{ success: boolean; estado: HorimetroEstadoDto; error?: string }>(
    `/api/locacoes/registros-horimetro/estado?${params.toString()}`,
  );
  assertSuccess(data);
  return data.estado;
}

export async function updateRegistroHorimetro(
  id: string,
  body: Partial<{
    tipo: RegistroHorimetroTipo;
    horimetroInformado: number;
    observacao: string;
    dataHora: string;
  }>,
): Promise<RegistroHorimetroDto> {
  const { data } = await api.patch<{ success: boolean; registro: RegistroHorimetroDto; error?: string }>(
    `/api/locacoes/registros-horimetro/${id}`,
    body,
  );
  assertSuccess(data);
  return data.registro;
}

export async function deleteRegistroHorimetro(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/registros-horimetro/${id}`,
  );
  assertSuccess(data);
}

export async function uploadRegistroHorimetroFoto(
  registroId: string,
  arquivo: File,
): Promise<RegistroHorimetroFotoDto> {
  const fd = new FormData();
  fd.append("arquivo", arquivo);
  const { data } = await api.post<{ success: boolean; foto: RegistroHorimetroFotoDto; error?: string }>(
    `/api/locacoes/registros-horimetro/${registroId}/fotos`,
    fd,
  );
  assertSuccess(data);
  return data.foto;
}

export async function deleteRegistroHorimetroFoto(registroId: string, fotoId: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/registros-horimetro/${registroId}/fotos/${fotoId}`,
  );
  assertSuccess(data);
}

export function registroHorimetroFotoUrl(registroId: string, fotoId: string): string {
  return `/api/locacoes/registros-horimetro/${registroId}/fotos/${fotoId}`;
}

export async function uploadAbastecimentoFoto(
  abastecimentoId: string,
  file: File,
): Promise<AbastecimentoFotoDto> {
  const form = new FormData();
  form.append("arquivo", file);
  const { data } = await api.post<{ success: boolean; foto: AbastecimentoFotoDto; error?: string }>(
    `/api/locacoes/abastecimentos/${abastecimentoId}/fotos`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  assertSuccess(data);
  return data.foto;
}

export async function deleteAbastecimentoFoto(abastecimentoId: string, fotoId: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(
    `/api/locacoes/abastecimentos/${abastecimentoId}/fotos/${fotoId}`,
  );
  assertSuccess(data);
}

export function abastecimentoFotoUrl(abastecimentoId: string, fotoId: string): string {
  return `/api/locacoes/abastecimentos/${abastecimentoId}/fotos/${fotoId}`;
}

export async function createAbastecimento(body: {
  maquinaId: string;
  data: string;
  horimetroAtual: number;
  litros: number;
  valorTotal: number;
  tipoCombustivel: CombustivelTipo;
  contratoId?: string;
  observacao?: string;
}): Promise<AbastecimentoDto> {
  const { data } = await api.post<{ success: boolean; abastecimento: AbastecimentoDto; error?: string }>(
    "/api/locacoes/abastecimentos",
    body,
  );
  assertSuccess(data);
  return data.abastecimento;
}

export async function updateAbastecimento(
  id: string,
  body: Partial<{
    data: string;
    horimetroAtual: number;
    litros: number;
    valorTotal: number;
    tipoCombustivel: CombustivelTipo;
    observacao?: string;
  }>,
): Promise<AbastecimentoDto> {
  const { data } = await api.patch<{ success: boolean; abastecimento: AbastecimentoDto; error?: string }>(
    `/api/locacoes/abastecimentos/${id}`,
    body,
  );
  assertSuccess(data);
  return data.abastecimento;
}

export async function deleteAbastecimento(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(`/api/locacoes/abastecimentos/${id}`);
  assertSuccess(data);
}

export async function fetchRelatorioUtilizacao(
  period: PeriodRange,
  opts?: { maquinaId?: string; maquinaIds?: string[]; contratoId?: string },
) {
  const params: Record<string, string> = { ...period };
  if (opts?.maquinaId) params.maquinaId = opts.maquinaId;
  else if (opts?.maquinaIds?.length) params.maquinaIds = opts.maquinaIds.join(",");
  if (opts?.contratoId) params.contratoId = opts.contratoId;
  const { data } = await api.get<{
    success: boolean;
    period: PeriodRange;
    rows: Array<{ maquinaId: string; maquinaNome: string; horasTrabalhadas: number }>;
    error?: string;
  }>("/api/locacoes/relatorios/utilizacao", { params });
  assertSuccess(data);
  return data;
}

export async function fetchRelatorioAbastecimentos(
  period: PeriodRange,
  opts?: { maquinaId?: string; maquinaIds?: string[]; contratoId?: string },
) {
  const params: Record<string, string> = { ...period };
  if (opts?.maquinaId) params.maquinaId = opts.maquinaId;
  else if (opts?.maquinaIds?.length) params.maquinaIds = opts.maquinaIds.join(",");
  if (opts?.contratoId) params.contratoId = opts.contratoId;
  const { data } = await api.get<{
    success: boolean;
    period: PeriodRange;
    litros: number;
    valor: number;
    rows: AbastecimentoDto[];
    error?: string;
  }>("/api/locacoes/relatorios/abastecimentos", { params });
  assertSuccess(data);
  return data;
}

export async function fetchRelatorioConsumo(period: PeriodRange, maquinaIds?: string[], contratoId?: string) {
  const params: Record<string, string> = { ...period };
  if (maquinaIds?.length) params.maquinaIds = maquinaIds.join(",");
  if (contratoId) params.contratoId = contratoId;
  const { data } = await api.get<{
    success: boolean;
    period: PeriodRange;
    ranking: OperationalMetricsDto["consumoPorContrato"];
    rankingMaquinas: OperationalMetricsDto["consumoPorMaquina"];
    error?: string;
  }>("/api/locacoes/relatorios/consumo", { params });
  assertSuccess(data);
  return data;
}

export async function fetchRelatorioLocacoes(
  period: PeriodRange,
  opts?: { obraId?: string; contratoId?: string },
) {
  const params: Record<string, string> = { ...period };
  if (opts?.obraId) params.obraId = opts.obraId;
  if (opts?.contratoId) params.contratoId = opts.contratoId;
  const { data } = await api.get<{
    success: boolean;
    period: PeriodRange;
    rows: LocacaoDto[];
    error?: string;
  }>("/api/locacoes/relatorios/locacoes", { params });
  assertSuccess(data);
  return data;
}

export interface LocacoesClientesSiengeResponse {
  clientes: SiengeClienteOption[];
  count: number;
  totalCount: number;
  cachedAt: number | null;
}

export async function fetchLocacoesClientesSienge(opts?: {
  q?: string;
  refresh?: boolean;
}): Promise<LocacoesClientesSiengeResponse> {
  const { data } = await api.get<{
    success: boolean;
    clientes: SiengeClienteOption[];
    count?: number;
    totalCount?: number;
    cachedAt?: number | null;
    error?: string;
  }>("/api/locacoes/clientes-sienge", {
    params: {
      ...(opts?.q ? { q: opts.q } : {}),
      ...(opts?.refresh ? { refresh: "1" } : {}),
    },
    timeout: 120_000,
  });
  assertSuccess(data);
  return {
    clientes: data.clientes ?? [],
    count: data.count ?? data.clientes?.length ?? 0,
    totalCount: data.totalCount ?? data.clientes?.length ?? 0,
    cachedAt: data.cachedAt ?? null,
  };
}

export async function createContrato(body: ContratoFormValues): Promise<ContratoDto> {
  const { data } = await api.post<{ success: boolean; contrato: ContratoDto; error?: string }>(
    "/api/locacoes/contratos",
    body,
  );
  assertSuccess(data);
  return data.contrato;
}

export async function updateContrato(
  id: string,
  body: Partial<ContratoFormValues> & {
    operadores?: ContratoOperadorDto[];
    status?: ContratoStatus;
    justificativaAlteracao?: string;
  },
): Promise<ContratoDto> {
  const { data } = await api.patch<{ success: boolean; contrato: ContratoDto; error?: string }>(
    `/api/locacoes/contratos/${id}`,
    body,
  );
  assertSuccess(data);
  return data.contrato;
}

export async function registrarReajusteValorCarrada(
  id: string,
  body: { valorUnitario: number; observacao?: string },
): Promise<ContratoDto> {
  const { data } = await api.post<{ success: boolean; contrato: ContratoDto; error?: string }>(
    `/api/locacoes/contratos/${id}/reajuste-valor-carrada`,
    body,
  );
  assertSuccess(data);
  return data.contrato;
}

export interface LeadTimeValorCarradaFaixaDto {
  vigencia: ValorCarradaVigencia;
  totalLancamentos: number;
  quantidadeCarradas: number;
  valorTotal: number;
  carradas: Array<{
    id: string;
    data: string;
    quantidade: number;
    valorUnitario: number;
    material: string;
    caminhaoId: string;
    horaRegistro: string;
    motorista: string;
  }>;
}

export async function fetchLeadTimeValorCarrada(
  contratoId: string,
): Promise<{ contratoId: string; faixas: LeadTimeValorCarradaFaixaDto[] }> {
  const { data } = await api.get<{
    success: boolean;
    contratoId: string;
    faixas: LeadTimeValorCarradaFaixaDto[];
    error?: string;
  }>(`/api/locacoes/contratos/${contratoId}/leadtime-valor-carrada`);
  assertSuccess(data);
  return { contratoId: data.contratoId, faixas: data.faixas ?? [] };
}

export async function deleteContrato(id: string): Promise<void> {
  const { data } = await api.delete<{ success: boolean; error?: string }>(`/api/locacoes/contratos/${id}`);
  assertSuccess(data);
}

export interface ContratoArquivadoFilters {
  q?: string;
  clienteSiengeId?: string;
  maquinaId?: string;
  deletedFrom?: string;
  deletedTo?: string;
}

export interface ContratoArquivadoDto {
  id: string;
  contrato: ContratoDto;
  deletedAt: string;
  deletedBy?: { userId: string; userName: string };
  locacoes: LocacaoDto[];
  manutencoes: ManutencaoDto[];
  registrosHorimetro: RegistroHorimetroDto[];
  abastecimentos: AbastecimentoDto[];
  medicoes: MedicaoMaquinaDto[];
  /** Carradas do contrato excluído (histórico; fora da aba Carradas ativa). */
  carradas?: import("./carradas").CarradaDto[];
}

function appendContratoArquivadoFilters(params: URLSearchParams, filters?: ContratoArquivadoFilters): void {
  if (!filters) return;
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  if (filters.clienteSiengeId?.trim()) params.set("clienteSiengeId", filters.clienteSiengeId.trim());
  if (filters.maquinaId?.trim()) params.set("maquinaId", filters.maquinaId.trim());
  if (filters.deletedFrom?.trim()) params.set("deletedFrom", filters.deletedFrom.trim());
  if (filters.deletedTo?.trim()) params.set("deletedTo", filters.deletedTo.trim());
}

export async function fetchContratosArquivados(filters?: ContratoArquivadoFilters): Promise<ContratoArquivadoDto[]> {
  const params = new URLSearchParams();
  appendContratoArquivadoFilters(params, filters);
  const qs = params.toString();
  const { data } = await api.get<{ success: boolean; arquivados: ContratoArquivadoDto[]; error?: string }>(
    `/api/locacoes/contratos-arquivados${qs ? `?${qs}` : ""}`,
  );
  assertSuccess(data);
  return data.arquivados;
}

export async function fetchContratoArquivado(id: string): Promise<ContratoArquivadoDto> {
  const { data } = await api.get<{ success: boolean; arquivado: ContratoArquivadoDto; error?: string }>(
    `/api/locacoes/contratos-arquivados/${id}`,
  );
  assertSuccess(data);
  return data.arquivado;
}

export async function updateContratoPipelineFase(
  id: string,
  fase: ContratoPipelineFase,
  extras?: { motivoEncerramento?: string; ultimoFollowUp?: string },
): Promise<ContratoDto> {
  const { data } = await api.patch<{ success: boolean; contrato: ContratoDto; error?: string }>(
    `/api/locacoes/contratos/${id}/pipeline`,
    { fase, ...extras },
  );
  assertSuccess(data);
  return data.contrato;
}

export async function aprovarRegistroHorimetro(id: string): Promise<RegistroHorimetroDto> {
  const { data } = await api.post<{ success: boolean; registro: RegistroHorimetroDto; error?: string }>(
    `/api/locacoes/registros-horimetro/${id}/aprovar`,
  );
  assertSuccess(data);
  return data.registro;
}

export async function rejeitarRegistroHorimetro(id: string, motivo: string): Promise<RegistroHorimetroDto> {
  const { data } = await api.post<{ success: boolean; registro: RegistroHorimetroDto; error?: string }>(
    `/api/locacoes/registros-horimetro/${id}/rejeitar`,
    { motivo },
  );
  assertSuccess(data);
  return data.registro;
}

export async function aprovarManutencao(id: string): Promise<ManutencaoDto> {
  const { data } = await api.post<{ success: boolean; manutencao: ManutencaoDto; error?: string }>(
    `/api/locacoes/manutencoes/${id}/aprovar`,
  );
  assertSuccess(data);
  return data.manutencao;
}

export async function rejeitarManutencao(id: string, motivo: string): Promise<ManutencaoDto> {
  const { data } = await api.post<{ success: boolean; manutencao: ManutencaoDto; error?: string }>(
    `/api/locacoes/manutencoes/${id}/rejeitar`,
    { motivo },
  );
  assertSuccess(data);
  return data.manutencao;
}

export const CONTRATO_STATUS_LABELS: Record<ContratoStatus, string> = {
  ativo: "Ativo",
  esperando_ativacao: "Esperando ativação",
  inativo: "Inativo",
  encerrado: "Encerrado",
  cancelado: "Cancelado",
};
